import { Head, Link } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  categoryLabel,
  type Crumb,
  OwnBadge,
  Pagination,
  ReviewButton,
  SearchForm,
  type PageMeta,
} from "./shared"

type BrandRow = {
  id: number
  name: string
  is_own: boolean
  tracked_category: string | null
  leaf_count: number
}

type Props = {
  manufacturer: { id: number; name: string; is_own: boolean }
  brands: BrandRow[]
  pagination: PageMeta
  filters: { search: string | null }
  breadcrumb: Crumb[]
}

export default function Brands({ manufacturer, brands, pagination, filters, breadcrumb }: Props) {
  const path = "/admin/product-master"
  const query = { manufacturer_id: String(manufacturer.id), search: filters.search }
  const description = `Merek di bawah manufaktur ${manufacturer.name}.`

  return (
    <>
      <Head title={`${manufacturer.name} — Produk Master`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${manufacturer.name} — Produk Master`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell width="wide">
        <Breadcrumb crumbs={breadcrumb} />
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <h1>{manufacturer.name}</h1>
              <OwnBadge isOwn={manufacturer.is_own} />
            </div>
            <ReviewButton />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <SearchForm path={path} query={query} initial={filters.search ?? ""} placeholder="Nama merek" />
        </div>

        {brands.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Tidak ada merek yang cocok.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Merek</th>
                    <th className="px-4 py-3 font-medium">Kategori</th>
                    <th className="px-4 py-3 font-medium">Produk (leaf)</th>
                    <th className="w-10 px-4 py-3" aria-label="Buka" />
                  </tr>
                </thead>
                <tbody>
                  {brands.map((b) => (
                    <tr key={b.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link href={`${path}?brand_id=${b.id}`} className="font-medium">
                          {b.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {categoryLabel(b.tracked_category) ? (
                          <Badge tone="neutral">{categoryLabel(b.tracked_category)}</Badge>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{b.leaf_count}</td>
                      <td className="px-4 py-3 text-ink-muted">
                        <Link href={`${path}?brand_id=${b.id}`} aria-label={`Buka ${b.name}`}>
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {brands.map((b) => (
                <Link
                  key={b.id}
                  href={`${path}?brand_id=${b.id}`}
                  className="rounded-lg border border-hairline bg-surface p-4"
                >
                  <div className="font-medium text-ink-body">{b.name}</div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {categoryLabel(b.tracked_category) ?? "Tanpa kategori"} · {b.leaf_count} produk
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <Pagination meta={pagination} path={path} query={query} />
      </AppShell>
    </>
  )
}
